import { HERO_SLIDES } from "@/config/heroSlides";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Same hero as the static `<section id="static-hero">` in index.html.
 * Mounted only when that node is absent (client navigation onto `/`).
 * A full load of `/` already has the node outside #root, so this must not render a second one.
 */
export function HomeStaticHero() {
  const { language, t } = useLanguage();
  const lang = language === "en" ? "en" : "pt";

  return (
    <section
      id="static-hero"
      data-react-hero="true"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        padding: "100px 0 40px",
        boxSizing: "border-box",
      }}
    >
      <div id="hero-slides" aria-hidden="false">
        {HERO_SLIDES.map((slide, index) => (
          <img
            key={slide.id}
            className={index === 0 ? "hero-slide is-active" : "hero-slide"}
            src={slide.mobile}
            srcSet={`${slide.mobile} 900w, ${slide.desktop} 1920w`}
            sizes="100vw"
            alt={index === 0 ? slide.alt[lang] : ""}
            ref={(image) => {
              image?.setAttribute("fetchpriority", index === 0 ? "high" : "low");
            }}
            loading={index === 0 ? "eager" : "lazy"}
            decoding="async"
            width={1920}
            height={1080}
            aria-hidden={index === 0 ? undefined : true}
            data-slide={slide.id}
          />
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg,hsla(210,40%,20%,.4),hsla(210,30%,20%,.3),hsla(210,40%,20%,.4))",
        }}
      />
      <div className="container mx-auto px-4 text-center relative z-10 max-w-4xl">
        <p
          data-static-hero-headline
          className="font-bold leading-tight mb-4 text-white max-w-4xl mx-auto"
          style={{
            fontSize: "clamp(1.875rem,5vw,3.75rem)",
            textShadow: "0 2px 4px rgba(0,0,0,.5)",
          }}
        >
          {t("hero.title")}
        </p>
        <p className="text-lg mb-6 max-w-3xl mx-auto text-white" style={{ textShadow: "0 1px 3px rgba(0,0,0,.4)" }}>
          {t("hero.subtitle")}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center", alignItems: "center" }}>
          <a
            id="hero-cta-primary"
            href="/pathways"
            className="inline-flex items-center justify-center min-h-[48px] px-6 py-4 rounded-md border-2 font-semibold shadow-lg"
            style={{
              borderColor: "hsl(43,74%,49%)",
              backgroundColor: "hsl(43,74%,36%)",
              color: "white",
              textDecoration: "none",
            }}
          >
            {t("hero.cta")}
          </a>
          <a
            id="hero-cta-secondary"
            href="/interactive"
            className="inline-flex items-center justify-center min-h-[48px] px-6 py-4 rounded-md border-2 font-semibold shadow-lg"
            style={{
              borderColor: "rgba(255,255,255,.6)",
              backgroundColor: "rgba(255,255,255,.15)",
              color: "white",
              textDecoration: "none",
              backdropFilter: "blur(4px)",
            }}
          >
            {t("hero.ctaSecondary")}
          </a>
        </div>
      </div>
    </section>
  );
}
